/**
 * Admin System Health — /app/admin/system-health
 *
 * Per-task heartbeat freshness grid. Powered by /api/system-health which
 * reads system_task_heartbeats (every @heartbeat-wrapped Celery task drops
 * a row on completion).
 *
 * Built in direct response to the May 12 2026 worker-poisoning incident
 * where the worker was alive but silently dead for 3 days because no
 * surface showed per-task completion.
 *
 * Tiles:
 *   - green border: healthy
 *   - amber border: stale (no success in 2× expected interval)
 *   - red border:   failing (most-recent attempt failed)
 */
import { useEffect, useMemo, useState } from 'react'
import { Activity, RefreshCw, Search, AlertTriangle, CheckCircle2, XCircle, HeartPulse, HeartCrack } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import PageHeader from '../components/PageHeader'

interface TaskRow {
  task_name: string
  last_success_at: string | null
  last_attempt_at: string | null
  last_attempt_status: string | null
  last_error: string | null
  last_duration_s: number
  expected_interval_s: number
  actual_interval_s: number | null
  failure_count_7d: number
  attempt_count_7d: number
  status: 'healthy' | 'stale' | 'failing'
}

interface WorkerBlock {
  worker_alive: boolean | null
  beat_alive: boolean | null
  last_any_heartbeat_at: string | null
  seconds_since_last_heartbeat: number | null
  verdict: string
}

interface Payload {
  status: 'ok' | 'worker_down' | 'degraded'
  worker: WorkerBlock
  tasks: TaskRow[]
  stale_count: number
  failing_count: number
  healthy_count: number
  generated_at: string
}

function fmtSeconds(s: number | null): string {
  if (s == null) return 'never'
  if (s < 60) return `${Math.round(s)}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  return `${h}h ago`
}

function relativeAge(iso: string | null): string {
  if (!iso) return 'never'
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0) return 'just now'
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

function fmtInterval(seconds: number | null): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  if (seconds < 3600) return `${(seconds / 60).toFixed(0)}m`
  return `${(seconds / 3600).toFixed(1)}h`
}

const STATUS_BORDER: Record<TaskRow['status'], string> = {
  healthy: 'border-emerald/50',
  stale:   'border-amber/60',
  failing: 'border-rose/60',
}

const STATUS_TEXT: Record<TaskRow['status'], string> = {
  healthy: 'text-emerald-light',
  stale:   'text-amber-light',
  failing: 'text-rose-light',
}

function StatusIcon({ status }: { status: TaskRow['status'] }) {
  if (status === 'healthy') return <CheckCircle2 className="w-4 h-4 text-emerald-light" />
  if (status === 'stale')   return <AlertTriangle className="w-4 h-4 text-amber-light" />
  return <XCircle className="w-4 h-4 text-rose-light" />
}

export default function AdminSystemHealthPage() {
  const [data, setData] = useState<Payload | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | TaskRow['status']>('all')

  async function load() {
    setLoading(true); setErr(null)
    try {
      const { data } = await api.get('/api/system-health')
      setData(data)
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'load failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const filtered = useMemo(() => {
    if (!data) return []
    const ql = q.trim().toLowerCase()
    return data.tasks.filter(t => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false
      if (ql && !t.task_name.toLowerCase().includes(ql)) return false
      return true
    })
  }, [data, q, statusFilter])

  const banner = useMemo(() => {
    if (!data) return null
    const { stale_count, failing_count } = data
    if (stale_count === 0 && failing_count === 0) return null
    const parts: string[] = []
    if (failing_count) parts.push(`${failing_count} tasks failing`)
    if (stale_count)   parts.push(`${stale_count} tasks stale`)
    return parts.join(' · ')
  }, [data])

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        icon={Activity} iconClassName="text-cyan" title="SYSTEM HEALTH"
        subtitle="per-task heartbeat freshness for every @heartbeat-wrapped Celery task"
        actions={
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} /> Refresh
          </button>
        }
      />

      <p className="text-sm text-text-secondary max-w-3xl">
        Per-task heartbeat freshness for every Celery task wrapped with{' '}
        <code className="text-cyan">@heartbeat</code>. Built after the
        May 12 2026 incident where the worker ran for 3 days without
        completing a single task — silently — because nothing tracked
        per-task completion.
      </p>

      {data?.worker && (
        data.worker.worker_alive === false ? (
          <div className="card p-4 border-2 border-rose/60 bg-rose/10 text-rose-light flex items-start gap-3">
            <HeartCrack className="w-7 h-7 text-rose-light shrink-0 mt-0.5" />
            <div>
              <div className="text-lg font-bold tracking-wide text-text-primary">
                ⚠ WORKER SILENT — no task heartbeats in {fmtSeconds(data.worker.seconds_since_last_heartbeat)}
              </div>
              <div className="text-sm text-text-secondary mt-1">
                The worker is likely crash-looping. <span className="font-semibold text-rose-light">Scheduled tasks are NOT running.</span>{' '}
                Check <code className="text-rose-light">docker logs cto-worker</code>.
              </div>
              <div className="text-xs text-text-muted mt-1">
                beat: {data.worker.beat_alive ? 'dispatching' : 'no recent pulse'} · {data.worker.verdict}
              </div>
            </div>
          </div>
        ) : data.worker.worker_alive === true ? (
          <div className="card p-3 border border-emerald/50 bg-emerald/10 text-emerald-light flex items-center gap-3">
            <HeartPulse className="w-6 h-6 text-emerald-light shrink-0" />
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <span className="font-semibold text-text-primary">Worker healthy</span>
              <span className="text-sm text-text-secondary">
                last heartbeat {fmtSeconds(data.worker.seconds_since_last_heartbeat)}
              </span>
              <span className="text-xs text-text-muted">
                beat {data.worker.beat_alive ? 'dispatching' : 'no dedicated pulse'}
              </span>
            </div>
          </div>
        ) : (
          <div className="card p-3 border border-amber/50 bg-amber/10 text-amber-light flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-light shrink-0" />
            <div>
              <span className="font-semibold text-text-primary">Worker liveness unknown</span>
              <span className="text-sm text-text-secondary ml-2">{data.worker.verdict}</span>
            </div>
          </div>
        )
      )}

      {banner && (
        <div className="card p-3 border border-rose/50 bg-rose/10 text-rose-light font-medium">
          {banner}
        </div>
      )}

      {err && (
        <div className="card p-3 border border-amber/50 bg-amber/10 text-amber-light">
          {err}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-3 gap-3">
          <div className="card p-4 border border-emerald/50">
            <div className="text-xs text-emerald-light">Healthy</div>
            <div className="text-2xl font-bold text-emerald-light">{data.healthy_count}</div>
          </div>
          <div className="card p-4 border border-amber/60">
            <div className="text-xs text-amber-light">Stale</div>
            <div className="text-2xl font-bold text-amber-light">{data.stale_count}</div>
          </div>
          <div className="card p-4 border border-rose/60">
            <div className="text-xs text-rose-light">Failing</div>
            <div className="text-2xl font-bold text-rose-light">{data.failing_count}</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-text-muted" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Filter by task name…"
            className="w-full pl-8 pr-3 py-2 rounded-lg bg-surface-card border border-border text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent"
          />
        </div>
        <div className="flex gap-1">
          {(['all', 'healthy', 'stale', 'failing'] as const).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={clsx(
                'px-3 py-1.5 rounded text-xs border transition-colors capitalize',
                statusFilter === s
                  ? 'bg-cyan/15 border-cyan/50 text-cyan'
                  : 'bg-surface-card border-border text-text-muted hover:text-text-primary hover:border-border-light',
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(t => (
          <div
            key={t.task_name}
            className={clsx('card p-4 border-2', STATUS_BORDER[t.status])}
          >
            <div className="flex items-center gap-2 mb-2">
              <StatusIcon status={t.status} />
              <span className={clsx('text-xs uppercase font-semibold', STATUS_TEXT[t.status])}>
                {t.status}
              </span>
              <span className="ml-auto text-xs text-text-muted">
                {t.attempt_count_7d} attempts · {t.failure_count_7d} fails (7d)
              </span>
            </div>
            <div className="font-mono text-sm text-text-primary mb-2 truncate" title={t.task_name}>
              {t.task_name}
            </div>
            <div className="text-xs text-text-muted space-y-0.5">
              <div>last success: <span className="text-text-secondary">{relativeAge(t.last_success_at)}</span></div>
              <div>last attempt: <span className="text-text-secondary">{relativeAge(t.last_attempt_at)}</span></div>
              <div>expected every: <span className="text-text-secondary">{fmtInterval(t.expected_interval_s)}</span></div>
              {t.actual_interval_s != null && (
                <div>last gap: <span className="text-text-secondary">{fmtInterval(t.actual_interval_s)}</span></div>
              )}
              <div>last duration: <span className="text-text-secondary">{t.last_duration_s.toFixed(2)}s</span></div>
              {t.last_error && (
                <div className="mt-2 text-rose-light break-words text-[11px] font-mono">
                  {t.last_error}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {data && (
        <div className="text-xs text-text-muted">
          generated at {data.generated_at}
        </div>
      )}
    </div>
  )
}
